import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaTrashAlt } from "react-icons/fa";
import Swal from "sweetalert2";
import { getData, postData, deleteData } from "../services/FetchNodeservices";
import Header from "../components/Header";
import Footer from "../components/Footer";

export default function InvoicePayment() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoiceData, setInvoiceData] = useState({
    invoice_number: "",
    invoice_date: "",
  });

  const [paymentDate, setPaymentDate] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [amount, setAmount] = useState("");
  const [payments, setPayments] = useState([]);

  // Fetch invoice and payments
  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const response = await getData(`api/invoice_master/${id}`);
        if (response?.success && response?.data) {
          const data = response.data;
          const formattedDate = data.invoice_date
            ? new Date(data.invoice_date).toLocaleDateString("en-GB")
            : "dd-mm-yyyy";
          setInvoiceData({
            invoice_number: data.invoice_number,
            invoice_date: formattedDate,
          });
        }
      } catch (error) {
        console.error("❌ Error fetching invoice:", error);
      }
    };

    const fetchPayments = async () => {
      try {
        const res = await getData(`api/payment_master/${id}`);
        if (res?.success) setPayments(res.data);
      } catch (err) {
        console.error("❌ Error fetching payments:", err);
      }
    };

    if (id) {
      fetchInvoice();
      fetchPayments();
    }
  }, [id]);

  // Add payment
  const handleAddPayment = async () => {
    if (!paymentDate || !paymentMode || !amount) {
      Swal.fire("Warning", "All fields are required", "warning");
      return;
    }

    const body = {
      invoice_id: id,
      payment_date: paymentDate,
      payment_mode: paymentMode,
      amount_paid: parseFloat(amount),
    };

    const res = await postData("api/payment_master", body);
    if (res?.success) {
      Swal.fire("Success", "Payment added successfully", "success");
      // Fetch updated list instead of manually pushing to handle IDs correctly from backend
      const resUpdated = await getData(`api/payment_master/${id}`);
      if (resUpdated?.success) setPayments(resUpdated.data);
      
      setPaymentDate("");
      setPaymentMode("");
      setAmount("");
    } else {
      Swal.fire("Error", res.message || "Failed to add payment", "error");
    }
  };

  // Delete payment
  const handleRemove = async (pid) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this payment?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (confirm.isConfirmed) {
      const res = await deleteData(`api/payment_master/${pid}`);
      if (res?.success) {
        Swal.fire("Deleted!", "Payment removed successfully", "success");
        setPayments(payments.filter((p) => p.id !== pid));
      } else {
        Swal.fire("Error", res.message || "Failed to delete payment", "error");
      }
    }
  };

  // Logic to handle closing tab or going back
  const handleClose = () => {
    if (window.history.length <= 1) {
      window.close();
    } else {
      navigate(-1);
    }
  };

  const totalAmount = payments.reduce((sum, p) => sum + Number(p.amount_paid), 0);

  return (
    <>
      {/* <Header /> */}
      <div className="bg-gray-100 min-h-screen p-6 mt-16 ml-0 md:ml-72">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-semibold text-gray-700">Invoice Payment</h1>
          <p className="text-sm text-gray-500">
            Home &gt; <span className="text-blue-600">Invoice Payment</span>
          </p>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4 border-b pb-2">
            Invoice Payment Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Invoice Number</label>
              <input
                type="text"
                value={invoiceData.invoice_number || ""}
                readOnly
                className="w-full border rounded-md px-3 py-2 bg-gray-100 text-gray-700"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Invoice Date</label>
              <input
                type="text"
                value={invoiceData.invoice_date || "dd-mm-yyyy"}
                readOnly
                className="w-full border rounded-md px-3 py-2 bg-gray-100 text-gray-700"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Payment Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="w-full border rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Payment Mode <span className="text-red-500">*</span>
              </label>
              <select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">Select Mode</option>
                <option value="Cash">Cash</option>
                <option value="Cheque">Cheque</option>
                <option value="Online">Online</option>
                <option value="UPI">UPI</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter Amount"
                className="w-full border rounded-md px-3 py-2"
              />
            </div>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
            >
              Cancel / Close
            </button>
            <button
              onClick={handleAddPayment}
              disabled={!amount || !paymentMode || !paymentDate}
              className={`px-4 py-2 rounded-md text-white font-medium ${
                amount && paymentMode && paymentDate
                  ? "bg-sky-500 hover:bg-sky-600"
                  : "bg-sky-300 cursor-not-allowed"
              }`}
            >
              Add Payment
            </button>
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full border text-sm text-gray-700">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="border px-4 py-2 text-left">S.No.</th>
                  <th className="border px-4 py-2 text-left">Date</th>
                  <th className="border px-4 py-2 text-left">Mode</th>
                  <th className="border px-4 py-2 text-left">Amount</th>
                  <th className="border px-4 py-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {payments.length > 0 ? (
                  payments.map((p, i) => (
                    <tr key={p.id || i} className="hover:bg-gray-50">
                      <td className="border px-4 py-2">{i + 1}</td>
                      <td className="border px-4 py-2">
                        {new Date(p.payment_date).toLocaleDateString("en-GB")}
                      </td>
                      <td className="border px-4 py-2">{p.payment_mode}</td>
                      <td className="border px-4 py-2">₹{p.amount_paid}</td>
                      <td className="border px-4 py-2 text-center">
                        <button
                          onClick={() => handleRemove(p.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <FaTrashAlt />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="5"
                      className="border px-4 py-3 text-center text-gray-500"
                    >
                      No payment records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-right">
            <div className="bg-sky-500 text-white inline-block px-6 py-2 rounded-md font-semibold">
              Total Paid: ₹{totalAmount}
            </div>
          </div>
        </div>
      </div>
      {/* <Footer /> */}
    </>
  );
}